// ═══════════════════════════════════════════════════════════════════════════
// Generic AI → EditorCommand translator seam (CC-2b)
// ═══════════════════════════════════════════════════════════════════════════
//
// CC-2a added the fail-closed `aiCallable` guard; CC-2b mounts the AI-command
// package behind the app's safe contour. The app must NOT depend on the
// block-specific entry points (`translateAICommandToDispatch` lives in
// `blockDispatcher.ts` and switches over block actions only). Instead it
// depends on this DOMAIN-AGNOSTIC seam:
//
//   - `AiCommand`            — the generic AI-command shape the safe contour
//                             accepts. TODAY it equals `BlockAiCommand` (the
//                             only implemented domain). CC-2c EXTENDS the union:
//                               export type AiCommand =
//                                 | BlockAiCommand
//                                 | HatchAiCommand
//                                 | ArrayAiCommand
//                                 | DimensionAiCommand
//                                 | …;
//   - `translateAiCommand`      / `translateAiCommandBatch` — the router the
//                             app mounts. Today it forwards to the block domain;
//                             CC-2c turns the forward into a per-domain switch
//                             (mirroring `translateAICommandToDispatch`).
//
// The seam is deliberately a forward (not a re-export) so that when CC-2c
// widens `AiCommand`, the `BlockAiCommand`-typed forward stops type-checking —
// forcing the domain-routing implementation to be written here, NOT in the
// app. The app mount (`apps/web/.../ai/aiSafeContour.ts`) never changes.
//
// Every translation path still runs the CC-2a `assertAiCallableAction` guard
// before producing any `EditorCommand` (it lives inside the per-domain
// translators), so the safe contour fails CLOSED for non-AI-callable targets
// regardless of which domain owns the action.

import { z } from "zod";

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
} from "@dxf-ai-editor/command-registry";

// `aiCommandSchema` / `aiCommandBatchSchema` live in the schema-only module (so
// the manifest derivation stays off the dispatch path). Imported here for the
// parse helpers below AND re-exported so the existing
// `import { aiCommandSchema } from "./aiDispatcher"` public API is unchanged.
import { aiCommandSchema, aiCommandBatchSchema } from "./aiCommandSchema";
import { type BlockAiCommand } from "./blockSchemas";
import {
  ENTITY_AI_ACTIONS,
  type EntityAiCommand,
} from "./entitySchemas";
import {
  isDrawModifyAiCommand,
  type DrawModifyAiCommand,
} from "./drawModifySchemas";
import {
  isViewSpaceAiCommand,
  type ViewSpaceAiCommand,
} from "./viewSpaceSchemas";
import {
  isMatchpropAiCommand,
  type MatchpropAiCommand,
} from "./matchpropSchemas";
import {
  isBaseModifyAiCommand,
  isBasePrimAiCommand,
  type BasePrimDomainAiCommand,
} from "./basePrimSchemas";
import { isLayerAiCommand, type LayerAiCommand } from "./layerSchemas";
import {
  advanceDispatchContext,
  type AiDispatchContext,
} from "./blockDispatcherShared";
import { translateAICommandToDispatch } from "./blockDispatcher";
import { translateEntityCommand } from "./entityDispatcher";
import { translateDrawModifyCommand } from "./drawModifyDispatcher";
import { translateViewSpaceCommand } from "./viewSpaceDispatcher";
import { translateMatchpropCommand } from "./matchpropDispatcher";
import { translateBasePrimCommand } from "./basePrimDispatcher";
import { translateLayerCommand } from "./layerDispatcher";

/**
 * The generic AI-command shape accepted by the safe contour.
 *
 * CC-2b shipped this as `BlockAiCommand` (the only wired domain then). CC-2c
 * widens it one sub-arm at a time: sub-arm 1 added the entity-creation domain
 * (text / mtext / hatch / dimension / mleader / revcloud); sub-arm 2 added the
 * DRAW-MODIFY domain (TRIM/EXTEND/OFFSET/MIRROR/SCALE/STRETCH/ARRAYRECT/
 * ARRAYEDIT); sub-arm 3 added the VIEW+SPACE domain (CREATE_VIEWPORT [MVIEW],
 * SWITCH_TO_MODEL_SPACE [MSPACE], SWITCH_TO_PAPER_SPACE [PSPACE], RENAME_LAYOUT
 * [LAYOUT R]); sub-arm 4 (FINAL) adds the BASE-PRIMITIVE (LINE/CIRCLE/ARC/PLINE)
 * + BASE-MODIFY (MOVE/COPY/ROTATE) domain. T7 (agent-bridge layer-mgmt) added
 * the LAYER-management domain (CREATE_LAYER/RENAME_LAYER/SET_ACTIVE_LAYER).
 * Keeping the app mount typed against `AiCommand` (not a single domain) means
 * a new domain never requires touching the app-side `aiSafeContour.ts` mount.
 */
export type AiCommand =
  | BlockAiCommand
  | EntityAiCommand
  | DrawModifyAiCommand
  | ViewSpaceAiCommand
  | MatchpropAiCommand
  | BasePrimDomainAiCommand
  | LayerAiCommand;

// Fast action-set membership test for domain routing. The block, entity, and
// draw-modify action namespaces are disjoint, so the `action` discriminator
// alone selects the owning domain.
const ENTITY_ACTION_SET: ReadonlySet<string> = new Set(ENTITY_AI_ACTIONS);

function isEntityAiCommand(command: AiCommand): command is EntityAiCommand {
  return ENTITY_ACTION_SET.has(command.action);
}

/**
 * Domain-routing top-level translator — the entry point the app mounts.
 *
 * Routes an AI command to the domain translator that owns its `action`, which
 * runs the CC-2a fail-closed guard (`assertAiCallable*Action`) before producing
 * any `EditorCommand`. Domains wired today: block (CC-2b) + draw-modify
 * (CC-2c sub-arm 2). CC-2c plugs further sibling domains in here without
 * touching the app-side mount.
 */
export function translateAiCommand(
  command: AiCommand,
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  // Per-domain routing keyed on the (DISJOINT) `action` literal sets. Each
  // domain translator runs its own CC-2a fail-closed guard
  // (`assertAiCallable*Action`) before producing any EditorCommand, so the seam
  // fails closed regardless of domain. CC-2c adds sibling routes here as each
  // sub-arm lands; the app mount never changes.
  if (isEntityAiCommand(command)) {
    return translateEntityCommand(command, context, descriptors);
  }
  if (isDrawModifyAiCommand(command)) {
    // Most draw-modify commands are self-contained (they reference entity ids
    // directly), but MIRROR needs `context.mirrtext` — the live MIRRTEXT sysvar
    // stamped onto the emitted command when the proposal omits its own value
    // (Codex #342 F2). Thread the context through so the seam honours a live
    // `SETVAR MIRRTEXT 1`.
    return translateDrawModifyCommand(command, context, descriptors);
  }
  if (isViewSpaceAiCommand(command)) {
    // View+space commands need the document layout state from `context`:
    // MSPACE/PSPACE resolve the layout to activate (active layout, else first)
    // and MVIEW validates its `spaceId` is a paper-space layout — both fail
    // closed when the context says no layout fits.
    return translateViewSpaceCommand(command, context, descriptors);
  }
  if (isMatchpropAiCommand(command)) {
    // MATCHPROP (Match Properties) is self-contained — it references the source
    // + target entity ids directly, so it needs no live document context. The
    // guarded dispatcher emits the `matchprop/set-source` + `matchprop/apply`
    // pair and fails closed if MATCHPROP is not AI-callable.
    return translateMatchpropCommand(command, context, descriptors);
  }
  if (isBasePrimAiCommand(command) || isBaseModifyAiCommand(command)) {
    // CC-2c sub-arm 4 (FINAL) — base primitives (create) + base modify. The
    // two predicates cover DISJOINT action sets (create vs MOVE/COPY/ROTATE)
    // and both route to the single guarded base-prim dispatcher: the create
    // commands resolve `spaceId` from `context`, the modify commands are
    // self-contained.
    return translateBasePrimCommand(command, context, descriptors);
  }
  if (isLayerAiCommand(command)) {
    // T7 (agent-bridge layer-mgmt) — CREATE_LAYER / RENAME_LAYER /
    // SET_ACTIVE_LAYER. Every action is self-contained (no live document /
    // active-space context needed — the reducer resolves table/entity state
    // from the document it is given), so the guarded LAYER dispatcher needs
    // no `context` beyond signature parity with the sibling domains.
    return translateLayerCommand(command, context, descriptors);
  }
  // Block domain (CC-2b).
  return translateAICommandToDispatch(command, context, descriptors);
}

/**
 * Batch variant — flatten-translates a list of AI commands in order via
 * {@link translateAiCommand} (so EACH command is domain-routed individually).
 * The app wraps the returned `EditorCommand[]` in a single history batch so one
 * undo reverses the whole AI turn.
 */
export function translateAiCommandBatch(
  commands: readonly AiCommand[],
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  // Flatten-translate in order via the domain-routing `translateAiCommand` so a
  // single batch can mix domains (e.g. a block INSERT, then an entity HATCH,
  // then a draw-modify ARRAYRECT) — each command is guarded + routed to its
  // owning domain. A non-AI-callable target anywhere in the batch throws before
  // any command is dispatched.
  //
  // The context is THREADED through the loop (`advanceDispatchContext`): when a
  // preceding command activates a space (SET_LAYOUT / MSPACE / PSPACE emit
  // `shell/activate-space`), the running context's `activeSpaceId` advances so a
  // LATER MSPACE/PSPACE resolves against the just-activated layout — not the
  // frozen initial context (#290-16). Single source of truth for the running
  // active space (the canonical activate-space command), no parallel tracking.
  const all: EditorCommand[] = [];
  let runningContext = context;
  for (const command of commands) {
    const emitted = translateAiCommand(command, runningContext, descriptors);
    all.push(...emitted);
    runningContext = advanceDispatchContext(runningContext, emitted);
  }
  return all;
}

// ═══════════════════════════════════════════════════════════════════════════
// Boundary parse — the single source of truth for validating AI proposals
// ═══════════════════════════════════════════════════════════════════════════
//
// `translateAiCommand(Batch)` assumes an ALREADY-VALID `AiCommand` (it reads
// fields raw and runs ONLY the CC-2a `aiCallable` action guard). It does NOT
// apply the per-schema Zod `.default(...)` / `.refine(...)` rules. Without a
// parse step, those rules are dead: a malformed / degenerate proposal slips
// past the AI boundary and either throws at the downstream contract parse
// (after a history batch is already open) or produces a wrong / no-op command.
//
// `aiCommandSchema` is the COMBINED discriminated union across all five domains
// — built by spreading each domain union's `.options` so there is ONE union and
// it stays in lock-step with the domains automatically (no re-listed members).
// The safe contour parses every proposal through it BEFORE translating, so
// defaults are materialised, refines enforced, and an invalid / unknown-action
// proposal is REJECTED fail-closed (no dispatch, no open batch).
//
// DEFINED in the schema-only `./aiCommandSchema` module (NOT inline here) so the
// capability manifest can derive from it WITHOUT importing this dispatcher
// module (which pulls in every domain translator at module scope). Imported at
// the top of this file (for `parseAiCommand` below) and re-exported here so the
// existing `import { aiCommandSchema } from "./aiDispatcher"` public API is
// unchanged.
export { aiCommandSchema, aiCommandBatchSchema };

/**
 * Typed error thrown when an AI proposal fails the boundary schema parse
 * (malformed payload, missing required field, failed refine, or unknown
 * action). Distinct from {@link NonAiCallableCommandError} (which is about the
 * aiCallable registry flag): this one is the DATA-shape rejection. Callers can
 * `instanceof`-narrow it to surface a structured rejection to the AI transcript
 * / audit trail. The original `ZodError` is preserved on `zodError`.
 */
export class AiCommandValidationError extends Error {
  /** The underlying Zod failure (full issue list on `.issues`). */
  readonly zodError: z.ZodError;
  /** The proposal's `action` discriminator, when it was a string. */
  readonly action: string | undefined;

  constructor(zodError: z.ZodError, action: string | undefined) {
    const summary = zodError.issues
      .map((issue) => `${issue.path.join(".") || "<root>"}: ${issue.message}`)
      .join("; ");
    super(
      `AI safe contour: proposal${
        action !== undefined ? ` "${action}"` : ""
      } failed schema validation; refusing to dispatch (fail-closed). ${summary}`,
    );
    this.name = "AiCommandValidationError";
    this.zodError = zodError;
    this.action = action;
  }
}

/** Read a candidate proposal's `action` discriminator for error context. */
function readAction(input: unknown): string | undefined {
  if (typeof input === "object" && input !== null && "action" in input) {
    const action = (input as { action: unknown }).action;
    return typeof action === "string" ? action : undefined;
  }
  return undefined;
}

/**
 * Parse a single AI proposal through {@link aiCommandSchema}. Returns the
 * normalised `AiCommand` (defaults applied, refines enforced); throws
 * {@link AiCommandValidationError} on any validation failure (fail-closed).
 */
export function parseAiCommand(input: unknown): AiCommand {
  const result = aiCommandSchema.safeParse(input);
  if (!result.success) {
    throw new AiCommandValidationError(result.error, readAction(input));
  }
  return result.data;
}

/**
 * Parse a BATCH of AI proposals through {@link aiCommandSchema}. Validates ALL
 * proposals first; throws {@link AiCommandValidationError} on the FIRST invalid
 * one (fail-closed — a single bad proposal rejects the whole turn before any
 * dispatch). Returns the normalised `AiCommand[]`.
 */
export function parseAiCommandBatch(inputs: readonly unknown[]): AiCommand[] {
  return inputs.map((input) => parseAiCommand(input));
}
